import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { FiX } from "react-icons/fi";
import "./NotificationCategories.css";
import { useSelector } from "react-redux";

const CreateCategoryModal = ({
  show,
  handleClose,
  onSuccess,
  editingCategory,
}) => {
  const API_URL =
    "https://estbn.com/EstbnNew/public/api/portal/notification-categories";

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [defaultChannels, setDefaultChannels] = useState([]);
  const [defaultOptIn, setDefaultOptIn] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [required, setRequired] = useState(false);
  const [iconFile, setIconFile] = useState(null);
  const [iconUrlPreview, setIconUrlPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const generateSlug = (categoryName) => {
    return categoryName
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, "")
      .trim()
      .replace(/\s+/g, "-");
  };

  const handleNameChange = (e) => {
    const newName = e.target.value;
    setName(newName);
    setSlug(generateSlug(newName));
  };

  const handleChannelChange = (e) => {
    const { value, checked } = e.target;
    setDefaultChannels((prev) =>
      checked ? [...prev, value] : prev.filter((channel) => channel !== value)
    );
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setIconFile(file);
    if (file) {
      setIconUrlPreview(URL.createObjectURL(file));
    } else {
      setIconUrlPreview(editingCategory?.icon_url || null);
    }
  };

  const resetForm = () => {
    setName("");
    setSlug("");
    setDescription("");
    setDefaultChannels([]);
    setDefaultOptIn(false);
    setIsActive(false);
    setRequired(false);
    setIconFile(null);
    setIconUrlPreview(null);
    setError(null);
  };

  useEffect(() => {
    if (show) {
      if (editingCategory) {
        setName(editingCategory.name || "");
        setSlug(
          editingCategory.slug || generateSlug(editingCategory.name || "")
        );
        setDescription(editingCategory.description || "");
        setDefaultChannels(editingCategory.default_channels || []);
        setDefaultOptIn(!!editingCategory.default_opt_in);
        setIsActive(!!editingCategory.is_active);
        setRequired(!!editingCategory.required);
        setIconFile(null);
        setIconUrlPreview(editingCategory.icon_url || null);
        setError(null);
      } else {
        resetForm();
      }
    }
  }, [show, editingCategory]);

  const onModalHide = () => {
    resetForm();
    handleClose();
  };
  const { user } = useSelector((state) => state.auth);
  const token = user?.token;

  const handleSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append("name", name);
    formData.append("slug", slug);
    formData.append("description", description);

    defaultChannels.forEach((channel) => {
      formData.append("default_channels[]", channel);
    });

    formData.append("default_opt_in", defaultOptIn ? "1" : "0");
    formData.append("required", required ? "1" : "0");
    formData.append("is_active", isActive ? "1" : "0");

    if (iconFile) {
      formData.append("icon", iconFile);
    }

    const method = editingCategory ? "POST" : "POST";
    const url = editingCategory ? `${API_URL}/${editingCategory.id}` : API_URL;

    if (editingCategory) {
      formData.append("_method", "PUT");
    }

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const contentType = response.headers.get("Content-Type");
      let responseData;

      if (contentType && contentType.includes("application/json")) {
        responseData = await response.json();
      } else {
        const textResponse = await response.text();
        console.error("Non-JSON response received:", textResponse);
        throw new Error(
          `Server responded with non-JSON format (HTTP Status: ${response.status}). ` +
            `Possible server error or misconfigured endpoint. Response: ${textResponse.substring(
              0,
              200
            )}...`
        );
      }

      if (!response.ok) {
        const errorMessage =
          responseData && responseData.message
            ? responseData.message
            : `HTTP Error ${response.status}: ${response.statusText}.`;
        setError(errorMessage);
        toast.error(errorMessage);
        return;
      }

      if (responseData.success) {
        const successMsg = editingCategory
          ? "Category updated successfully!"
          : "Category created successfully!";
        if (onSuccess) {
          onSuccess(successMsg);
        }
        onModalHide();
      } else {
        const errorMessage =
          responseData.message || "Operation failed due to an unknown reason.";
        setError(errorMessage);
        toast.error(errorMessage);
      }
    } catch (err) {
      console.error("Error during category operation:", err);
      const errorMessage =
        err.message ||
        "An unexpected error occurred. Please check your network connection.";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!show) {
    return null;
  }

  return (
    <div className="nc-modal-overlay">
      <div className="nc-modal-content">
        <div className="nc-modal-header">
          <h2 className="nc-modal-title">
            {editingCategory ? "Edit Category" : "Create New Category"}
          </h2>
          <button
            className="nc-modal-close-btn"
            onClick={onModalHide}
            disabled={loading}
            aria-label="Close modal"
          >
            <FiX />
          </button>
        </div>

        <div className="nc-modal-body">
          {loading && (
            <div className="nc-loading-message">
              <div className="nc-custom-spinner"></div>
              {editingCategory ? "Saving Changes..." : "Creating Category..."}
            </div>
          )}
          {error && <p className="text-danger">{error}</p>}

          <form onSubmit={handleSubmit}>
            <div className="nc-form-group">
              <label htmlFor="formCategoryName">Name</label>
              <input
                type="text"
                id="formCategoryName"
                placeholder="Add Category name"
                className="nc-form-control"
                value={name}
                onChange={handleNameChange}
                required
                disabled={loading}
              />
            </div>

            <div className="nc-form-group">
              <label htmlFor="formCategorySlug">Slug</label>
              <input
                type="text"
                id="formCategorySlug"
                className="nc-form-control"
                value={slug}
                readOnly
                disabled={loading}
              />
            </div>

            <div className="nc-form-group">
              <label htmlFor="formCategoryDescription">Description</label>
              <textarea
                id="formCategoryDescription"
                rows="5"
                placeholder="Enter category description"
                className="nc-form-control nc-textarea"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                disabled={loading}
              ></textarea>
            </div>

            <div className="nc-form-group">
              <label htmlFor="categoryImageInput">Category image</label>
              <div className="nc-image-upload-area">
                <div className="nc-image-placeholder">
                  {iconUrlPreview ? (
                    <img src={iconUrlPreview} alt="Category Icon Preview" />
                  ) : (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="40"
                      height="40"
                      fill="#adb5bd"
                      className="bi bi-image"
                      viewBox="0 0 16 16"
                    >
                      <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                      <path d="M12.97 14H2.03c-.088 0-.17-.002-.25-.004C.909 13.935 0 13.045 0 12V4c0-.982.909-1.874 1.78-1.996.08-.002.162-.004.25-.004H14c.982 0 1.874.909 1.996 1.78.002.08.004.162.004.25v8c0 .982-.909 1.874-1.78 1.996-.08.002-.162-.004-.25-.004zM1.5 2h13a.5.5 0 0 1 .5.5v8a.5.5 0 0 1-.5.5H1.5a.5.5 0 0 1-.5-.5v-8a.5.5 0 0 1 .5-.5zM.5 4v8c0 .02.01.04.013.06L.5 4zm15 .06c-.003-.02-.013-.04-.013-.06V4l.013.06zM8.5 6a.5.5 0 0 1 .5-.5h5a.5.5 0 0 1 0 1H9a.5.5 0 0 1-.5-.5zM4 11.5a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H4.5a.5.5 0 0 1-.5-.5zM.5 12h15v.5c0 .02-.01.04-.013.06L.5 12zM.5 14h15v.5c0 .02-.01.04-.013.06L.5 14z" />
                    </svg>
                  )}
                </div>
                <div className="nc-image-info">
                  <span className="nc-image-file-name">
                    {iconFile
                      ? iconFile.name
                      : editingCategory?.icon_url
                      ? "Existing image"
                      : "Upload an image. Max size 2MB"}
                  </span>
                </div>
                <button
                  type="button"
                  className="nc-btn nc-browse-button"
                  onClick={() =>
                    document.getElementById("categoryImageInput").click()
                  }
                  disabled={loading}
                >
                  Browse
                </button>
                <input
                  type="file"
                  id="categoryImageInput"
                  accept="image/*"
                  onChange={handleFileChange}
                  style={{ display: "none" }}
                  disabled={loading}
                />
              </div>
            </div>

            <div className="nc-form-group">
              <label>Default Channels</label>
              <div className="nc-checkbox-group">
                <label htmlFor="email-checkbox">
                  <input
                    type="checkbox"
                    id="email-checkbox"
                    value="email"
                    checked={defaultChannels.includes("email")}
                    onChange={handleChannelChange}
                    disabled={loading}
                  />
                  <span className="nc-checkbox-label-text">Email</span>
                </label>
                <label htmlFor="sms-checkbox">
                  <input
                    type="checkbox"
                    id="sms-checkbox"
                    value="sms"
                    checked={defaultChannels.includes("sms")}
                    onChange={handleChannelChange}
                    disabled={loading}
                  />
                  <span className="nc-checkbox-label-text">SMS</span>
                </label>
                <label htmlFor="push-checkbox">
                  <input
                    type="checkbox"
                    id="push-checkbox"
                    value="push"
                    checked={defaultChannels.includes("push")}
                    onChange={handleChannelChange}
                    disabled={loading}
                  />
                  <span className="nc-checkbox-label-text">Push</span>
                </label>
              </div>
            </div>

            <div className="nc-form-group nc-checkbox-row-group">
              <label htmlFor="default-opt-in-checkbox">
                <input
                  type="checkbox"
                  id="default-opt-in-checkbox"
                  checked={defaultOptIn}
                  onChange={(e) => setDefaultOptIn(e.target.checked)}
                  disabled={loading}
                />
                <span className="nc-checkbox-label-text">Default Opt-In</span>
              </label>
              <label htmlFor="active-status-checkbox">
                <input
                  type="checkbox"
                  id="active-status-checkbox"
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                  disabled={loading}
                />
                <span className="nc-checkbox-label-text">Active Category</span>
              </label>
            </div>

            <div className="nc-form-group">
              <label htmlFor="required-category-checkbox">
                <input
                  type="checkbox"
                  id="required-category-checkbox"
                  checked={required}
                  onChange={(e) => setRequired(e.target.checked)}
                  disabled={loading}
                />
                <span className="nc-checkbox-label-text">
                  Required Category
                </span>
              </label>
              <p className="nc-form-text">
                Users cannot opt-out of required categories
              </p>
            </div>

            <div className="nc-modal-footer">
              <button
                type="button"
                className="nc-btn nc-btn-cancel"
                onClick={onModalHide}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="nc-btn nc-btn-save"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="nc-custom-spinner-small"></div>
                    <span>Saving...</span>
                  </>
                ) : editingCategory ? (
                  "Save Changes"
                ) : (
                  "Save"
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateCategoryModal;
